import type { FC } from "react";
import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { useActionSheet } from "@9amhealth/shared/src/molecule/ActionSheet/useActionSheet";
import { IconDotsHorizontal } from "src/constants/icons";
import translate from "src/lib/translate";
import Translate from "src/ui/components/Translate/Translate";
import { ActionConfig } from "src/state/AppointmentsBloc/AppointmentsBloc";

const Button = styled.button`
  display: flex;
  align-items: center;
  padding: 0.8rem 1rem;
  gap: 6px;
  border-radius: calc(infinity * 1px);
  background: var(--color-cream-darker);
  font-weight: 500;

  font-size: 0.75rem;
  line-height: 1rem;

  cursor: pointer;

  &:active,
  &:focus,
  &:hover {
    background: var(--color-gray-light);
  }
`;

interface AppointmentActionProps {
  config: ActionConfig | null;
}

export const AppointmentAction: FC<AppointmentActionProps> = (props) => {
  const { config } = props;
  if (!config) {
    return null;
  }

  return (
    <Button
      onClick={(event) => {
        event.stopPropagation();
        config.onClick();
      }}
      className="appointment-action"
    >
      {config.icon}
      <Translate msg={config.label} />
    </Button>
  );
};

export const MoreActionsMenu: FC<{
  onSeeDetails: () => void;
  onCancelVisit: () => void;
}> = ({ onSeeDetails, onCancelVisit }) => {
  const buttons = useMemo(
    () => [
      {
        id: "see-details",
        title: translate("appointment.action.label.seeDetails"),
        icon: "details" as const
      },
      {
        id: "cancel-visit",
        title: translate("appointment.action.label.cancelVisit"),
        style: "destructive" as const,
        icon: "delete" as const
      }
    ],
    []
  );

  const handleSelect = (buttonId: string) => {
    if (buttonId === "see-details") {
      onSeeDetails();
    } else if (buttonId === "cancel-visit") {
      onCancelVisit();
    }
  };

  const { show } = useActionSheet({
    buttons,
    iconPosition: "right",
    onSelect: handleSelect
  });

  return (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        void show();
      }}
      aria-label={translate("appointment.action.label.seeDetails")}
    >
      <IconDotsHorizontal />
    </Button>
  );
};
